import { EmailCodeSignInForm, SignInLayout, SignInNotice } from '@bcecloud/portal-ui';
import { api } from '@/lib/api';
import { APP_NAME, LEGACY_PORTAL_LABEL, LEGACY_PORTAL_URL } from '@/lib/launch';

/**
 * On-demand provisioning for the unknown-account path: anyone already in the
 * classic portal's user directory with a staff-app role (BCE team or
 * contractor) gets their BCE-team Clerk account created server-side, and the
 * form retries the code send — the My Events pattern, with the directory
 * check replacing open sign-up. Returns whether to retry.
 */
async function provisionStaffAccount(email: string): Promise<boolean> {
  const response = await api.post<{ data: { ready: boolean } }>('/auth/staff-provision', {
    email,
  });
  return response.data.data.ready === true;
}

/**
 * The hero gradient, as palette tokens rather than hexes — they are
 * interpolated straight into a CSS `linear-gradient()`, so the screen tracks
 * whatever bce-theme.css pins the brand to.
 *
 * Named from the palette, not from the *roles* (`--color-brand`,
 * `--color-brand-sidebar`): both roles now resolve to royal, which would give
 * a gradient with nothing to travel between.
 */
const BRAND_ROYAL = 'var(--color-bce-royal)';
const BRAND_NAVY = 'var(--color-bce-navy)';

/**
 * The front door to the internal admin.
 *
 * The same split-screen sign-in as the client portal — one component in
 * portal-ui, rendered here in BCE's own colors — rather than Clerk's stock
 * card. Staff meeting BCEcloud for the first time should land on something
 * that looks like BCE.
 *
 * The headline and subcopy are written to outlast the launch: this is the
 * sign-in screen from now on, and copy that introduces the product as "new"
 * dates itself within weeks. What *is* transitional — the classic portal
 * still running — sits in the notice, where it can be removed without
 * rewriting the screen. The audience is the BCE team plus contract
 * facilitators, who sign in with whatever address BCE has on file for them —
 * so the copy must not promise a company-domain address.
 *
 * Sign-in is email-code only and **never falls through to sign-up**: the API
 * treats every account in the BCE-team Clerk instance as staff, so an account
 * that could be self-created here would be self-granted access. What IS
 * self-served is provisioning for people already in the directory: an
 * unknown address is checked server-side and, when it belongs to an active
 * staff-app-role user, the Clerk account is created on demand and the code
 * send retried — see {@link provisionStaffAccount}.
 *
 * @returns The sign-in screen.
 */
export function AdminSignInScreen(): React.ReactElement {
  return (
    <SignInLayout
      masthead={
        <span className="text-[27px] leading-none font-bold tracking-[-2px] text-brand">
          {APP_NAME}
        </span>
      }
      mastheadLabel="INTERNAL ADMIN"
      eyebrow="BEST CORPORATE EVENTS"
      headline={'The operation,\nin one place.'}
      subcopy="Events, shipments, recaps, programs and client portals — everything BCE runs, in one system."
      notice={
        <SignInNotice>
          <span aria-hidden="true">✓</span> <b>For the BCE team and facilitators.</b> Sign in with
          the email BCE has on file for you — we&rsquo;ll send a one-time code, so there&rsquo;s no
          new password to remember. Still need the old portal?{' '}
          <a href={LEGACY_PORTAL_URL} className="font-bold text-brand underline underline-offset-2">
            {LEGACY_PORTAL_LABEL}
          </a>{' '}
          is running as usual.
        </SignInNotice>
      }
      footerLeft="Powered by Best Corporate Events"
      footerRight="Internal admin"
      hero={{
        fromColor: BRAND_ROYAL,
        toColor: BRAND_NAVY,
        quote: 'Every event, every shipment, every client — one system.',
        statValue: 'BCE team + facilitators',
        statLabel: 'Clients see their events on My Events.',
      }}
    >
      <EmailCodeSignInForm
        emailLabel="Your email"
        emailPlaceholder="The address BCE has on file for you"
        allowSignUp={false}
        onUnknownAccount={provisionStaffAccount}
        unknownAccountMessage="No BCE account for that address. Use the email the classic portal has on file for you — or ask an admin to add you."
      />
    </SignInLayout>
  );
}
